import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatNumberToLocal, toastError, toastSuccess } from "../../helpers";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router";
import { hPlanCalculation } from "../../helpers";
import { usePurchaseHeliorPlanMutation } from "../../app/features/apiSlice";
import { BANK_PAYMENT_METHODS, CURRENCY_SYMBOLS } from "../../Constants";
import CurrencyValue from "../CurrencyValue";

const PurchaseSummary = ({ paymentData }) => {
  const { t } = useTranslation();
  const [purchaseData, setPurchaseData] = useState({});
  const navigate = useNavigate();
  const { lang } = useParams();

  const currencySymbol =
    CURRENCY_SYMBOLS[purchaseData.currency] ?? purchaseData.currency;

  const [purchaseHeliorPlan, { isLoading }] = usePurchaseHeliorPlanMutation();

  const isBankPayment = BANK_PAYMENT_METHODS.includes(
    purchaseData.paymentMethod,
  );
  let txFee = 0;
  if (!isBankPayment) {
    txFee = purchaseData.txCryptoFee;
  } else {
    txFee =
      purchaseData.paymentMethod === "Credit Card"
        ? purchaseData.txFeeCC
        : purchaseData.txBankFee;
  }

  const handleConfirmPurchase = async () => {
    try {
      const data = {
        acceptTNC: purchaseData.acceptTNC || purchaseData.P01,
        P01: purchaseData.P01,
        P02: purchaseData.P02,
        P03: purchaseData.P03,
        P04: purchaseData.P04,
        P05: purchaseData.P05,
        P06: purchaseData.P06,
        P07: purchaseData.P07,
        P08: purchaseData.P08,
        C01: purchaseData.C01,
        C02: purchaseData.C02,
        C03: purchaseData.C03,
        B01: purchaseData.B01,
        B02P: purchaseData.B02P,
        B03: purchaseData.B03,
        discountCode: purchaseData.discountCode,
        ingot: purchaseData.ingot,
        isDocSplit: purchaseData.docSplit,
        language: lang,
        nroInitialPayments: purchaseData.initialPaymentNumber,
        nroRecurringPayments: purchaseData.recurringPaymentNumber,
        paymentAmount: purchaseData.recurringPaymentAmount,
        paymentMethod: purchaseData.paymentMethod,
        periodicityOfPayment: purchaseData.periodicityOfPayment,
        currency: purchaseData.currency,
      };

      const response = await purchaseHeliorPlan({
        data,
        planId: purchaseData?.planId,
      });
      if (response?.error) {
        toastError(response, t("error.serverNotResponding"));
        if(response?.error?.status === 308){
          navigate(`/${lang}/products`);
        }
      } else {
        toastSuccess(response?.data?.message);
        navigate(`/${lang}/payment-success`);
      }
    } catch (err) {
      toastError(err, t("error.serverNotResponding"));
    }
  };

  useEffect(() => {
    if (paymentData) {
      setPurchaseData(paymentData);
    }
  }, [paymentData]);

  return (
    <div className="summary-wrapper p-3">
      <h6 className="mb-3">{t("summary")}</h6>
      <ul className="list-unstyled">
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("recurringAmountPaid")} </span>
          <span className="text-nowrap">
            <CurrencyValue
              amount={purchaseData.totalRecurringAmount}
              rate={purchaseData.exchangeRate}
              currency={purchaseData.currency}
            />
          </span>
        </li>
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("numberOfInitialPayments2")} </span>
          <span className="text-nowrap">
            {purchaseData.initialPaymentNumber || 0}
          </span>
        </li>
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("numberOfRecurringPayments2")} </span>
          <span className="text-nowrap">
            {purchaseData.recurringPaymentNumber || 0}
          </span>
        </li>
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("paymentAmount")} </span>
          <span className="text-nowrap">
            <CurrencyValue
              amount={purchaseData.recurringPaymentAmount}
              rate={purchaseData.exchangeRate}
              currency={purchaseData.currency}
            />
          </span>
        </li>
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("docShareOfTotal")} </span>
          <span className="text-nowrap">
            <CurrencyValue
              amount={purchaseData.dossierOpeningCharge}
              rate={purchaseData.exchangeRate}
              currency={purchaseData.currency}
            />
          </span>
        </li>
        {purchaseData.docSplit && (
          <li className="d-flex justify-content-between mb-3">
            <span className="w-50 lh-sm">{t("docSplitAmount")} </span>
            <span className="text-nowrap">
              <CurrencyValue
                amount={purchaseData.docSplitAmount}
                rate={purchaseData.exchangeRate}
                currency={purchaseData.currency}
              />
            </span>
          </li>
        )}
        <li className="d-flex justify-content-between mb-3">
          <span className="w-50 lh-sm">{t("initialPaymentAmount")} </span>
          <span className="text-nowrap">
            <CurrencyValue
              amount={purchaseData.initialPaymentAmount}
              rate={purchaseData.exchangeRate}
              currency={purchaseData.currency}
            />
          </span>
        </li>
        <hr />
        <li className="d-flex justify-content-between text-uppercase mb-1">
          <span className="w-50 lh-sm">{t("totalPayment")}</span>
          <span className="text-nowrap">
            <CurrencyValue
              amount={purchaseData.totalPayment}
              rate={purchaseData.exchangeRate}
              currency={purchaseData.currency}
            />
          </span>
        </li>
        {purchaseData?.stampDuty && purchaseData?.stampDuty > 0 ? (
          <li className="d-flex justify-content-between text-uppercase mb-1">
            <span className="w-50 lh-sm">{t("stampDuty")}</span>
            <span className="text-nowrap">
              <CurrencyValue
                amount={purchaseData?.stampDuty}
                rate={purchaseData.exchangeRate}
                currency={purchaseData.currency}
              />
            </span>
          </li>
        ) : (
          ""
        )}
        <li className="d-flex justify-content-between text-uppercase mb-1">
          <span>{t("paymentMethod")}</span>{" "}
          <span className="text-nowrap">
            {t(`${purchaseData.paymentMethod}`)}
          </span>
        </li>
        <hr />
        <li className="d-flex justify-content-between text-uppercase mb-2">
          <span>{t("totalPayment")}</span>
          <span className="text-nowrap">
            <CurrencyValue
              amount={
                purchaseData.totalPayment + (purchaseData?.stampDuty || 0)
              }
              rate={purchaseData.exchangeRate}
              currency={purchaseData.currency}
            />
          </span>
        </li>
      {purchaseData.paymentMethod && (
        <li className="d-flex justify-content-between text-uppercase mb-3">
          <span className="w-50 lh-sm">
            {t("totalPurchaseWith")}
            {lang === "en" &&
              `${
                BANK_PAYMENT_METHODS.includes(purchaseData.paymentMethod)
                  ? purchaseData.paymentMethod?.toLowerCase() === 'credit card' ? purchaseData.txFeeCC : purchaseData.txBankFee
                  : purchaseData.txCryptoFee
              }%`}
            {t("TXFee")}
            {lang === "it" &&
              `${
                BANK_PAYMENT_METHODS.includes(purchaseData.paymentMethod)
                  ? purchaseData.paymentMethod?.toLowerCase() === 'credit card' ? purchaseData.txFeeCC : purchaseData.txBankFee
                  : purchaseData.txCryptoFee
              }%`}
          </span>
          <span className="text-nowrap">
            <CurrencyValue
              amount={
                purchaseData.totalPaymentWithTX + (purchaseData?.stampDuty || 0)
              }
              rate={purchaseData.exchangeRate}
              currency={purchaseData.currency}
            />
          </span>
        </li>
      )}
      </ul>
      <Button
        variant="primary-outline"
        className="w-100 d-flex align-items-center justify-content-center mt-5"
        onClick={handleConfirmPurchase}
      >
        {t("xOneTncFlags.confirmOrderWithObligation") || t("confirmPurchase")}
        {isLoading && <Spinner size="sm" className="ms-2" animation="grow" />}
      </Button>
    </div>
  );
};

export default PurchaseSummary;
